import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import { AuthService } from '@core/services/auth/auth.service';
import { LayoutService } from '@core/services/layout/layout.service';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { NavbarComponent, SubNavbarComponent } from './components';
import { LAYOUT_ROUTES } from './constants';
import { SUB_NAVBAR_CONFIG } from './constants/sub-navbar.constants';
import { MainLayoutStore } from './main-layout.store';

@Component({
    selector: 'app-main-layout',
    standalone: true,
    imports: [
        NavbarComponent,
        SubNavbarComponent,
        RouterOutlet,
        CommonModule,
        ConfirmationModalComponent,
    ],
    templateUrl: './main-layout.component.html',
    styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit {
    /**
     * Injected router instance for navigation.
     */
    private router = inject(Router);

    /** Layout service that drives shell-wide view state. */
    private layoutService = inject(LayoutService);

    /**
     * Auth service — exposes the cross-tab logout notice signal to the template.
     */
    public authService = inject(AuthService);

    /**
     * Signal to track the current URL.
     */
    private currentUrl = signal<string>(this.router.url);

    /**
     * Computed sub-navbar configuration based on the current URL.
     */
    private subNavbarConfig = computed(() => {
        const url = this.currentUrl();
        return SUB_NAVBAR_CONFIG.find((c) => url.includes(c.baseUrl));
    });

    /**
     * Computed visibility of the sub-navbar component.
     */
    public showSubNavbar = computed(() => !!this.subNavbarConfig());

    /**
     * Computed selected module or navigation item.
     * Stores the label and parent label for breadcrumb and sub-navbar display.
     */
    public selectedModule = computed(() => {
        const url = this.currentUrl();
        const breadcrumbTitle = MainLayoutStore.breadcrumb();

        if (url.startsWith(LAYOUT_ROUTES.PROFILE_SETTINGS)) {
            return {
                label: breadcrumbTitle || 'Profile Settings',
                parent: 'Dashboard',
                url: LAYOUT_ROUTES.DASHBOARD,
            };
        }

        const config = this.subNavbarConfig();
        if (!config) {
            return { label: '', parent: '', url: '' };
        }

        const isDetailPage = !!(config.detailRegex && config.detailRegex.test(url));

        const breadcrumb = isDetailPage ? breadcrumbTitle || config.label : config.label;

        return {
            label: breadcrumb,
            parent: isDetailPage ? config.label : '',
            url: config.baseUrl,
        };
    });

    /**
     * Holds the current selected module (when using the ACTUAL top navbar) since we're setting the selectedModule as well using the sub-page navigation
     * @description This primarily holds the value for main page navigation
     */
    public currentSelectedModule = signal<string>('');

    /** Shell overflow state driven by the active page. */
    public readonly overflowHidden = this.layoutService.overflowHidden;

    ngOnInit(): void {
        // Update sub-navbar on route changes
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.currentUrl.set(event.url);

                if (!event.url.includes('/dashboard')) {
                    this.layoutService.setOverflowHidden(false);
                }
            });
    }

    /**
     * Handles navigation and UI updates when a selection changes in the navbar.
     * Updates the selected module state and manages routing for specific items.
     * @param event The selection event containing label and optional parent.
     */
    public onSelectionChange(event: { label: string; parent?: string }): void {
        const config = SUB_NAVBAR_CONFIG.find((c) => c.label === event.label);
        if (config) {
            this.router.navigate([config.baseUrl]);
        }
    }
}
